import { useEffect, useState } from "react";
import { Crescent } from "@/atoms/crescent";
import type { ReactNode } from "react";

/**
 * The moment between the payment and the receipt.
 *
 * checkout 1.4's motion says the crescent draws ONCE over the long duration after payment and
 * the receipt crossfades in. `.ds-fade` was on the receipt and never fired, because the element
 * mounted already at its resting state: a class with no from-state animates nothing.
 *
 * So the beat is real now. The 48 crescent draws once in the form's place, and when it has
 * finished the receipt mounts with `.ds-reveal`, which is the one entrance this app allows. It
 * is the only place in Gymkee Pay where anything is celebrated, and it is a smile drawn once,
 * not a tick in a green disc.
 *
 * Under `prefers-reduced-motion` the wait is skipped entirely and the receipt is simply there:
 * the crescent's own class paints its final state at once, so holding the page on it would be a
 * beat with nothing happening in it.
 */

export function PaymentSettled({ children }: { children: ReactNode }) {
  const [drawn, setDrawn] = useState(() => prefersReducedMotion());

  useEffect(() => {
    if (drawn) return;
    const timer = window.setTimeout(() => setDrawn(true), DRAW_MS);
    return () => window.clearTimeout(timer);
  }, [drawn]);

  if (!drawn) {
    return (
      <div className="py-gk-16 flex items-center justify-center" role="status">
        <Crescent size={48} delay={0} repeat={false} tone="complete" />
      </div>
    );
  }

  return <div className="ds-reveal">{children}</div>;
}

/** motion.duration.long, the token the template names for this draw. */
const DRAW_MS = 700;

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined") return true;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}
